import React from "react";
import { Control } from "react-hook-form";
import AppSelect, { AppSelectController } from "../../Core/AppSelect/AppSelect";
import { useCollectiveAgreements } from "../../../networking/collective-agreement.hooks";
import { CollectiveAgreement } from "../../../models/Consultant";

interface CollectiveAgreementDropdownProps {
	value?: string;
	onChange?: (value: string) => void;
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	errors?: string;
	mandatory?: boolean;
	width?: string;
	margin?: string;
	name?: string;
	showKnowledgeBaseForTagOnHover?: string;
}

/**
 * Dropdown component for selecting a collective agreement.
 * Fetches real collective agreements from the API and displays them.
 *
 * This component replaces the legacy hard-coded COLLECTIVE_AGREEMENTS config.
 */
const CollectiveAgreementDropdown: React.FC<CollectiveAgreementDropdownProps> = ({
	value,
	onChange,
	label = "consultant.collectiveAgreement",
	placeholder = "consultant.collectiveAgreementPlaceholder",
	disabled = false,
	errors,
	mandatory = false,
	width,
	margin,
	name,
	showKnowledgeBaseForTagOnHover,
}) => {
	const { collectiveAgreementList, isLoading } = useCollectiveAgreements({
		page: 0,
		pageSize: 100,
	});

	const options = collectiveAgreementList?.collectiveAgreements || [];

	return (
		<AppSelect<CollectiveAgreement>
			name={name}
			options={options}
			value={value}
			onChange={onChange}
			label={label}
			placeholder={placeholder}
			disabled={disabled || isLoading}
			errors={errors}
			mandatory={mandatory}
			width={width}
			margin={margin}
			getOptionLabel={(option) => option.name}
			getOptionValue={(option) => option.id}
			showKnowledgeBaseForTagOnHover={showKnowledgeBaseForTagOnHover}
		/>
	);
};

export default CollectiveAgreementDropdown;

interface CollectiveAgreementDropdownControllerProps extends CollectiveAgreementDropdownProps {
	control: Control<any, any>;
	name: string;
	rules?: object;
}

/**
 * Controller version of CollectiveAgreementDropdown for use with react-hook-form.
 */
export const CollectiveAgreementDropdownController: React.FC<
	CollectiveAgreementDropdownControllerProps
> = ({ control, name, rules, onChange: onChangeExternal, ...rest }) => {
	const { collectiveAgreementList, isLoading } = useCollectiveAgreements({
		page: 0,
		pageSize: 100,
	});

	const options = collectiveAgreementList?.collectiveAgreements || [];

	return (
		<AppSelectController<CollectiveAgreement>
			control={control}
			name={name}
			rules={rules}
			onChange={onChangeExternal}
			options={options}
			disabled={rest.disabled || isLoading}
			getOptionLabel={(option) => option.name}
			getOptionValue={(option) => option.id}
			{...rest}
		/>
	);
};
